import { StrictMode, Component } from 'react';
import { createRoot } from 'react-dom/client';
import App from './App';
import { installPublisherAuthFetch } from './lib/publisherAuthFetch';
import './index.css';

installPublisherAuthFetch();

class RootErrorBoundary extends Component<
  { children: React.ReactNode },
  { hasError: boolean; errorMessage?: string; errorStack?: string; componentStack?: string }
> {
  state = {
    hasError: false,
    errorMessage: undefined,
    errorStack: undefined,
    componentStack: undefined,
  };

  static getDerivedStateFromError(error: unknown) {
    return { hasError: true, errorMessage: error instanceof Error ? error.message : String(error) };
  }

  componentDidCatch(error: unknown, info: { componentStack?: string }) {
    const errorStack = error instanceof Error ? error.stack : undefined;
    const componentStack = typeof info?.componentStack === 'string' ? info.componentStack : undefined;
    this.setState({ errorStack, componentStack });
    console.error('Promotion console failed to render:', error, info);
  }

  render() {
    if (this.state.hasError) {
      return (
        <div className="flex min-h-screen items-center justify-center bg-zinc-50 p-6">
          <div className="w-full max-w-2xl rounded-xl border border-zinc-200 bg-white p-6">
            <div className="text-lg font-semibold text-zinc-900">Promotion Console launch error</div>
            <div className="mt-2 text-sm text-zinc-600">{this.state.errorMessage || 'Unknown error'}</div>
            {(this.state.errorStack || this.state.componentStack) && (
              <pre className="mt-4 max-h-80 overflow-auto whitespace-pre-wrap rounded-lg border border-zinc-200 bg-zinc-50 p-3 text-xs text-zinc-800">
                {this.state.errorStack ? `\n[stack]\n${this.state.errorStack}` : ''}
                {this.state.componentStack ? `\n[componentStack]\n${this.state.componentStack}` : ''}
              </pre>
            )}
            <button
              className="mt-4 rounded-lg bg-zinc-100 px-4 py-2 text-sm font-medium text-zinc-900 hover:bg-zinc-200"
              onClick={() => {
                localStorage.removeItem('summio-admin-auth');
                window.location.reload();
              }}
            >
              Clear auth and reload
            </button>
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}

createRoot(document.getElementById('root')!).render(
  <StrictMode>
    <RootErrorBoundary>
      <App />
    </RootErrorBoundary>
  </StrictMode>
);
